"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FirebaseAuthenticationManager = void 0;
var tslib_1 = require("tslib");
var app_1 = require("firebase/app");
var auth_1 = require("firebase/auth");
var constants_1 = require("../../../../constants/constants");
var sdk_1 = require("../../../../sdk/sdk");
var profilesManager_1 = require("../../../storage/profiles/types/profilesManager");
/**
 * Inheritance from UserManagement class
 * @class
 * @implements {UserManagementProvider}
 * @remarks This class provides the implementation for firebase management operations.
 */
var FirebaseAuthenticationManager = /** @class */ (function () {
    function FirebaseAuthenticationManager() {
        this.waitForAuth = function () {
            return new Promise(function (resolve, reject) {
                var auth = (0, auth_1.getAuth)();
                var unsubscribe = auth.onAuthStateChanged(function (user) {
                    unsubscribe();
                    if (user) {
                        resolve(user);
                    }
                    else {
                        reject(new Error('No user is logged in'));
                    }
                });
            });
        };
    }
    FirebaseAuthenticationManager.prototype.register = function (email, password) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var auth, user, uid, accessToken, newUser, error_1;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        auth = (0, auth_1.getAuth)(FirebaseAuthenticationManager.firebaseApp);
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, (0, auth_1.createUserWithEmailAndPassword)(auth, email, password)
                            // TODO: On web, `getIdToken` fails but `accessToken` is available on the user. Verify this during web implementation.
                            // @ts-ignore
                        ];
                    case 2:
                        user = (_a.sent()).user;
                        uid = user.uid, accessToken = user.accessToken;
                        // Save Firebase (authentication) accessToken in LocalStorage
                        // so we can get it in the authorization service
                        localStorage.setItem(constants_1.authAccessTokenLS, accessToken);
                        newUser = {
                            userID: uid,
                            email: email,
                        };
                        return [2 /*return*/, newUser];
                    case 3:
                        error_1 = _a.sent();
                        console.error('Firebase register failed:', error_1);
                        throw error_1;
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    FirebaseAuthenticationManager.prototype.updateUserDetails = function (userDetails) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var currentUser, email, displayName, uid, photoURL, updatedUser, error_2;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        currentUser = (0, auth_1.getAuth)().currentUser;
                        if (!currentUser) {
                            throw new Error('User not authenticated');
                        }
                        return [4 /*yield*/, (0, auth_1.updateProfile)(currentUser, userDetails)];
                    case 1:
                        _a.sent();
                        email = currentUser.email, displayName = currentUser.displayName, uid = currentUser.uid, photoURL = currentUser.photoURL;
                        updatedUser = {
                            userID: uid,
                            email: email || '',
                            name: displayName || '',
                            photoURL: photoURL || '',
                        };
                        return [2 /*return*/, updatedUser];
                    case 2:
                        error_2 = _a.sent();
                        console.error('Firebase update profile failed:', error_2);
                        throw error_2;
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    FirebaseAuthenticationManager.prototype.login = function (loginEmail, password) {
        var _a, _b;
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var auth, user, token, email, uid, displayName, profilesManager, userHasProfiles, image, authenticatedUser, error_3;
            return tslib_1.__generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        _c.trys.push([0, 6, , 7]);
                        auth = (0, auth_1.getAuth)(FirebaseAuthenticationManager.firebaseApp);
                        return [4 /*yield*/, (0, auth_1.signInWithEmailAndPassword)(auth, loginEmail, password)];
                    case 1:
                        user = (_c.sent()).user;
                        return [4 /*yield*/, user.getIdToken()
                            // Save Firebase (authentication) accessToken in LocalStorage
                            // so we can get it in the authorization service
                        ];
                    case 2:
                        token = _c.sent();
                        // Save Firebase (authentication) accessToken in LocalStorage
                        // so we can get it in the authorization service
                        localStorage.setItem(constants_1.authAccessTokenLS, token);
                        if (!user) {
                            throw new Error('Failed to login with provided credentials.');
                        }
                        email = user.email, uid = user.uid, displayName = user.displayName;
                        profilesManager = sdk_1.VelvetSDK.Storage.Profiles.service;
                        return [4 /*yield*/, profilesManager.userHasProfiles(uid)];
                    case 3:
                        userHasProfiles = _c.sent();
                        image = (_a = sdk_1.VelvetSDK.Configuration.configuration.profileImages[0].data[0]) !== null && _a !== void 0 ? _a : profilesManager_1.defaultProfileImage;
                        if (!!userHasProfiles) return [3 /*break*/, 5];
                        return [4 /*yield*/, profilesManager.createProfile({
                                name: displayName,
                                image: image,
                            })];
                    case 4:
                        _c.sent();
                        _c.label = 5;
                    case 5:
                        authenticatedUser = {
                            userID: uid,
                            email: email || '',
                            name: displayName || '',
                            photoURL: ((_b = auth.currentUser) === null || _b === void 0 ? void 0 : _b.photoURL) || '',
                        };
                        return [2 /*return*/, authenticatedUser];
                    case 6:
                        error_3 = _c.sent();
                        console.error('Firebase login failed:', error_3);
                        throw error_3;
                    case 7: return [2 /*return*/];
                }
            });
        });
    };
    FirebaseAuthenticationManager.prototype.logout = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var auth, currentUser, error_4;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 3, , 4]);
                        auth = (0, auth_1.getAuth)(FirebaseAuthenticationManager.firebaseApp);
                        currentUser = auth.currentUser;
                        if (!currentUser) return [3 /*break*/, 2];
                        return [4 /*yield*/, (0, auth_1.signOut)(auth)];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2: return [3 /*break*/, 4];
                    case 3:
                        error_4 = _a.sent();
                        console.error('Firebase logout failed:', error_4);
                        throw error_4;
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    FirebaseAuthenticationManager.prototype.resetPassword = function (email) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.sendResetPasswordEmail(email)];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    FirebaseAuthenticationManager.prototype.deactivateUser = function (userID) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                // Implementation for deactivating user here
                console.log('Successfully deactivated user ' + userID);
                return [2 /*return*/];
            });
        });
    };
    FirebaseAuthenticationManager.prototype.deleteUser = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var auth, currentUser, error_5;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 3, , 4]);
                        auth = (0, auth_1.getAuth)(FirebaseAuthenticationManager.firebaseApp);
                        currentUser = auth.currentUser;
                        if (!currentUser) return [3 /*break*/, 2];
                        return [4 /*yield*/, (0, auth_1.deleteUser)(currentUser)];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2: return [3 /*break*/, 4];
                    case 3:
                        error_5 = _a.sent();
                        console.error('Failed to delete Firebase:', error_5);
                        throw error_5;
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    FirebaseAuthenticationManager.prototype.initializeFirebase = function (name) {
        try {
            var firebaseConfig = sdk_1.VelvetSDK.appConfig.services.authentication.config;
            FirebaseAuthenticationManager.firebaseApp = (0, app_1.initializeApp)(firebaseConfig, name);
        }
        catch (error) {
            console.error('Firebase configuration failed:', error);
            throw error;
        }
    };
    FirebaseAuthenticationManager.prototype.sendResetPasswordEmail = function (userEmail) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var auth, response;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        auth = (0, auth_1.getAuth)(FirebaseAuthenticationManager.firebaseApp);
                        return [4 /*yield*/, (0, auth_1.sendPasswordResetEmail)(auth, userEmail)];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, response];
                }
            });
        });
    };
    FirebaseAuthenticationManager.prototype.fetchSignInMethodsForEmail = function (userEmail) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var auth, response;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        auth = (0, auth_1.getAuth)(FirebaseAuthenticationManager.firebaseApp);
                        return [4 /*yield*/, (0, auth_1.fetchSignInMethodsForEmail)(auth, userEmail)];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, response];
                }
            });
        });
    };
    FirebaseAuthenticationManager.prototype.signInWithCustomToken = function (token) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var auth, response, userToken;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        auth = (0, auth_1.getAuth)(FirebaseAuthenticationManager.firebaseApp);
                        return [4 /*yield*/, (0, auth_1.signInWithCustomToken)(auth, token)];
                    case 1:
                        response = _a.sent();
                        return [4 /*yield*/, response.user.getIdToken()
                            // Save Firebase (authentication) accessToken in LocalStorage
                            // so we can get it in the authorization service
                        ];
                    case 2:
                        userToken = _a.sent();
                        // Save Firebase (authentication) accessToken in LocalStorage
                        // so we can get it in the authorization service
                        localStorage.setItem(constants_1.authAccessTokenLS, userToken);
                        return [2 /*return*/, response];
                }
            });
        });
    };
    FirebaseAuthenticationManager.prototype.getUser = function () {
        var auth = (0, auth_1.getAuth)(FirebaseAuthenticationManager.firebaseApp);
        var currentUser = auth === null || auth === void 0 ? void 0 : auth.currentUser;
        return currentUser !== null && currentUser !== void 0 ? currentUser : null;
    };
    FirebaseAuthenticationManager.prototype.getAccessToken = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var currentUser, _a;
            return tslib_1.__generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _b.trys.push([0, 3, , 4]);
                        return [4 /*yield*/, this.waitForAuth()];
                    case 1:
                        currentUser = _b.sent();
                        if (!currentUser)
                            return [2 /*return*/, null];
                        return [4 /*yield*/, currentUser.getIdToken(true)];
                    case 2: return [2 /*return*/, _b.sent()];
                    case 3:
                        _a = _b.sent();
                        return [2 /*return*/, null];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    return FirebaseAuthenticationManager;
}());
exports.FirebaseAuthenticationManager = FirebaseAuthenticationManager;
